import { QueryParams } from 'common/urls/QueryParams';
import { Slugs } from 'common/urls/Slugs';
import { useMemo } from 'react';
import { RouteChildrenProps, useRouteMatch } from 'react-router';
import { useFilterQueryParams } from 'src/shared/design-system/DishFirst/DishModal/utils';
import { Routes } from 'src/shared/Routes';
import { getParam, getSlug, getSluggedPath, locationInRoutes } from 'src/shared/utils/RouteUtilities';

type UseGetFoodItemPathProps = {
  foodItemSlug?: string | null;
  foodItemShefId?: string | null;
  location: RouteChildrenProps['location'];
  match: RouteChildrenProps['match'];
  isEditingOrder: boolean;
};

export const useGetFoodItemPath = (props: UseGetFoodItemPathProps) => {
  const { foodItemSlug, foodItemShefId, location, match, isEditingOrder } = props;
  const isEditingMultiShefOrder = locationInRoutes(location, [
    Routes.CONSUMER_ORDER_GROUP_ORDER_EDIT,
    Routes.CONSUMER_ORDER_EDIT_GROUP_ORDER_FOOD_ITEM,
  ]);

  const exploreSegmentId = getParam(location, QueryParams.EXPLORE_SEGMENT_ID);
  const idSlug = isEditingOrder ? getSlug(match, Slugs.ID) : foodItemShefId ?? getSlug(match, Slugs.ID);
  const shefIdSlug = getSlug(match, Slugs.SHEF_ID);
  const querySlug = getSlug(match, Slugs.QUERY);
  const { filtersQueryParams } = useFilterQueryParams();

  // Check to see if we are in the shef page
  const consumerOrderOneTimeMatch = useRouteMatch(Routes.CONSUMER_ORDER_ONE_TIME);

  const { shefPath, foodItemPath } = useMemo(() => {
    const slugs = [
      { slug: Slugs.ID, value: idSlug },
      { slug: Slugs.QUERY, value: querySlug },
      { slug: Slugs.SHEF_ID, value: shefIdSlug },
    ];
    if (foodItemSlug) {
      slugs.push({ slug: Slugs.NAME, value: foodItemSlug });
    }

    if (isEditingOrder) {
      if (isEditingMultiShefOrder) {
        return {
          shefPath: getSluggedPath(Routes.CONSUMER_ORDER_GROUP_ORDER_EDIT, slugs),
          foodItemPath: getSluggedPath(Routes.CONSUMER_ORDER_EDIT_GROUP_ORDER_FOOD_ITEM, slugs),
        };
      }
      return {
        shefPath: getSluggedPath(Routes.CONSUMER_ORDER_EDIT_LINE_ITEMS, slugs),
        foodItemPath: getSluggedPath(Routes.CONSUMER_ORDER_EDIT_FOOD_ITEM, slugs),
      };
    }

    return {
      shefPath: getSluggedPath(Routes.CONSUMER_ORDER_ONE_TIME, slugs),
      foodItemPath: getSluggedPath(Routes.CONSUMER_ORDER_FOOD_ITEM, slugs, {
        [QueryParams.EXPLORE_SEGMENT_ID]: exploreSegmentId,
        ...filtersQueryParams,
      }),
    };
  }, [consumerOrderOneTimeMatch, exploreSegmentId, foodItemSlug, idSlug, querySlug, isEditingMultiShefOrder]);
  return { shefPath, foodItemPath };
};
